/**
 * ADOBE CONFIDENTIAL
 * __________________
 * Copyright 2023 Adobe
 * All Rights Reserved.
 * __________________
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 */

import { FunctionComponent, VNode } from 'preact';
import {
  HTMLAttributes,
  useState,
  useMemo,
  useCallback,
  useEffect,
  JSX,
} from 'preact/compat';
import { classes } from '@adobe-commerce/elsie/lib';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  ColorSwatch,
  ImageSwatch,
  TextSwatch,
  Picker,
  PickerOption,
  ImageNodeRenderProps,
} from '@adobe-commerce/elsie/components';

import '@/pdp/components/Swatches/Swatches.css';

const supportedTypes = ['text', 'image', 'color', 'dropdown'];

const mapToPickerOptions = (items: OptionValue[]): PickerOption[] => {
  return items.map((item: OptionValue) => ({
    ...item,
    text: item.label,
    disabled: !item.inStock,
  }));
};

type OptionValue = {
  id: string;
  label: string;
  inStock: boolean;
  value: string;
  selected?: boolean;
};

export type Option = {
  id: string;
  type: (typeof supportedTypes)[number];
  label: string;
  required?: boolean;
  multiple?: boolean;
  items: OptionValue[];
};

type Selection = { [id: string]: { label: string; value: string } };

function optionUIDsToSelection(
  optionUIDs: string[],
  options: Array<Option>
): Selection {
  return optionUIDs.reduce((accum, optionUID) => {
    const option = options?.find((option) =>
      option.items.find((item) => item.id === optionUID)
    );
    if (!option) return accum;

    const item = option.items.find((item) => item.id === optionUID);

    return {
      ...accum,
      [option.id]: {
        label: item!.label,
        value: item!.id,
      },
    };
  }, {});
}

export interface SwatchesProps extends HTMLAttributes<HTMLDivElement> {
  options: Array<Option>;
  hideSelectedValue?: boolean;
  disablePreselections?: boolean;
  defaultOptions?: string[];
  selectionsToUpdate?: Option[];
  onValues?: (uids: Selection, current: string) => void;
  onErrors?: (errors: { [id: string]: string }) => void;
  selectedUIDs?: string[];
  imageSwatchNode?: VNode | ((props: ImageNodeRenderProps) => JSX.Element);
}

export const Swatches: FunctionComponent<SwatchesProps> = ({
  options,
  hideSelectedValue,
  disablePreselections,
  selectedUIDs,
  onValues,
  onErrors,
  defaultOptions,
  selectionsToUpdate,
  className,
  children,
  imageSwatchNode,
  ...props
}) => {
  // i18n
  const RequiredLabel = useText('PDP.Swatches.Required.label').label;
  const ChooseOptionLabel = useText('PDP.Swatches.ChooseOption.label').label;

  const [selections, setSelections] = useState<Selection>(() => {
    const initial = options?.reduce((accum, option) => {
      const { items } = option;
      const defaultSelection = items?.find((item) =>
        defaultOptions?.includes(item.id)
      );
      const selected = items?.find((item) => item.selected);
      if (defaultSelection) {
        return {
          ...accum,
          [option.id]: {
            label: defaultSelection.label,
            value: defaultSelection.id,
          },
        };
      } else if (selected) {
        return {
          ...accum,
          [option.id]: { label: selected.label, value: selected.id },
        };
      }
      return accum;
    }, {} as Selection);

    return initial;
  });

  // set selected options from props
  useEffect(() => {
    if (selectedUIDs) {
      const selection = optionUIDsToSelection(selectedUIDs, options);
      // if the internal state is already the same as the props, do nothing
      if (JSON.stringify(selection) === JSON.stringify(selections)) return;

      setSelections((prev) => {
        let next = {
          ...prev,
          ...selection,
        };
        onValues?.(next);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUIDs]);

  // this only applies for the case with anchor options
  // update selections in the swatches if they are updated in the parent component
  useEffect(() => {
    selectionsToUpdate?.forEach((selectionToUpdate) => {
      setSelections((prev) => {
        const selectedItem = selectionToUpdate?.items?.find(
          (item) => item.selected
        );
        if (!selectedItem) return prev;

        const next = {
          ...prev,
          [selectionToUpdate.id]: {
            label: selectedItem?.label,
            value: selectedItem?.id,
          },
        };
        return next;
      });
    });
  }, [selectionsToUpdate, setSelections]);

  // Update invalid options
  useMemo(() => {
    const errors = options?.reduce((accum, option) => {
      let valueSelected = false;
      option?.items?.forEach((item) => {
        const selected = selections[option.id]?.value === item.id;
        if (selected) {
          valueSelected = true;
        }
      });

      // Validate required
      if (option.required && !valueSelected) {
        accum[option.id] = RequiredLabel;
      }
      return accum;
    }, {} as { [id: string]: string });

    // Call the callback
    onErrors?.(errors);

    return errors;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selections]);

  const handleOnSelection = useCallback(
    (id: string, label: string, optionId: string) => {
      setSelections((prev) => {
        let next = {
          ...prev,
          ...(id && { [id]: { label, value: optionId } }),
        };

        if (label === ChooseOptionLabel) {
          delete next[id];
        }

        // Call the callback
        onValues?.(next, optionId);

        // Return the next state
        return next;
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onValues]
  );

  // Function to handle the picker selection
  const handlePickerSelect = useCallback(
    (optionId: string, selection: any) => {
      const label = selection.selectedOptions[0]?.label ?? '';
      handleOnSelection(optionId, label, selection.value);
    },
    [handleOnSelection]
  );

  const Swatches = useCallback(
    ({ items, id, required, type, selected, label }: any) => {
      const uniqueId = `pdp-swatch-${id}=${Math.random().toString(36)}`;

      return (
        items?.length > 0 && (
          <div className={classes(['pdp-swatches__options'])}>
            {type?.toLowerCase() === 'dropdown' ? (
              <Picker
                id={uniqueId}
                name={id}
                aria-label={label}
                placeholder={
                  disablePreselections ? ChooseOptionLabel : undefined
                }
                handleSelect={(event) => handlePickerSelect(id, event.target)}
                options={mapToPickerOptions(items)}
                value={selected}
              />
            ) : (
              items?.map((option: any) => {
                const common = {
                  key: option.id,
                  id: `${uniqueId}-${option.id}`,
                  name: id,
                  value: option.value,
                  label: option.label,
                  groupAriaLabel: label,
                  selected: selected === option.id,
                  outOfStock: !option.inStock,
                  // API may return null, we default to true as all swatches are required.
                  //  we still comply to this in case the api return "false".
                  required: required === null ? true : required,
                  onValue: () => {
                    handleOnSelection(id, option.label, option.id);
                  },
                };

                switch (type) {
                  case 'text':
                    return <TextSwatch {...common} label={option.label} />;

                  case 'image':
                    return (
                      <ImageSwatch
                        {...common}
                        src={option.value}
                        alt={option.label}
                        imageNode={imageSwatchNode}
                      />
                    );

                  case 'color':
                    return (
                      <ColorSwatch
                        {...common}
                        size="large"
                        color={option.value}
                      />
                    );

                  default:
                    console.warn(`Invalid swatch type ${type}`);
                    return null;
                }
              })
            )}
          </div>
        )
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ChooseOptionLabel, handleOnSelection, handlePickerSelect]
  );

  return (
    <div {...props} className={classes(['pdp-swatches', className])}>
      {options?.map(({ type, label, id, multiple, required, items }) => {
        // Only render supported types
        if (!supportedTypes.includes(type)) {
          console.warn(`Invalid swatch type ${type}`);
        }

        if (multiple) {
          console.warn(
            'Multiple selection swatches are not currently supported.'
          );
        }

        return (
          <div
            key={`swatch-item-${id}`}
            id={`swatch-item-${id}`}
            data-slot-key={`product-swatch--${id}`}
            className="pdp-swatches__field"
          >
            <div className="pdp-swatches__field__label">
              {selections[id]
                ? hideSelectedValue
                  ? `${label}`
                  : `${label}: ${selections[id].label}`
                : label}
            </div>

            <Swatches
              id={id}
              type={type}
              required={required}
              items={items}
              label={label} //label referring to the name of the swatchGroup
              selected={selections[id]?.value}
            />
          </div>
        );
      })}
    </div>
  );
};
